import React from 'react';
import { Box, Flex, Heading, Radio, RadioGroup, Stack, Text } from '@chakra-ui/react';
import { useForm, useWatch } from 'react-hook-form';
import InfoTooltip from './InfoTooltip';

export interface IDigitalRule {
    name: string;
    downloadLimit: number | null;
    downloadLimitUsed: number | null;
    resolution: number | null;
    id: number;
    videoResolution?: string | null;
    videoDownloadLimit?: number | null;
    videoDownloadLimitUsed?: number | null;
    slideshowResolution?: string | null;
    slideshowDownloadLimit?: number | null;
    slideshowDownloadLimitUsed?: number | null;
}

interface IDigitalRules {
    control: ReturnType<typeof useForm>['control'];
    digitalRules: IDigitalRule[];
    isSlideshowDownload?: boolean;
    isVideoDownload?: boolean;
    register: ReturnType<typeof useForm>['register'];
    translateFilter: SpAngularJs.ITranslateFilter;
}

const DigitalRules: React.FC<IDigitalRules> = ({
    control,
    digitalRules,
    isSlideshowDownload = false,
    isVideoDownload = false,
    register,
    translateFilter: t
}) => {
    const socialSize = 900;
    const value = useWatch({ control, name: 'selectedDigitalRule' });
    const radioRegister = register('selectedDigitalRule', {
        required: t('selectOption'),
        valueAsNumber: true
    });

    // A digital rule carries independent limits for photos, videos and slideshows, so resolve
    // the limit fields for whichever media type is being downloaded.
    const getDownloadLimit = (digitalRule: IDigitalRule) => {
        if (isVideoDownload) {
            return digitalRule.videoDownloadLimit ?? null;
        }

        if (isSlideshowDownload) {
            return digitalRule.slideshowDownloadLimit ?? null;
        }

        return digitalRule.downloadLimit;
    };

    const getDownloadLimitUsed = (digitalRule: IDigitalRule) => {
        if (isVideoDownload) {
            return digitalRule.videoDownloadLimitUsed ?? null;
        }

        if (isSlideshowDownload) {
            return digitalRule.slideshowDownloadLimitUsed ?? null;
        }

        return digitalRule.downloadLimitUsed;
    };

    const isDisabled = (downloadLimit: number | null, downloadLimitUsed: number | null) => {
        // <= 0, not === 0 - usage can exceed the limit (e.g. it was lowered after some
        // downloads already happened), and that must still disable the option rather than
        // leaving it selectable because the exact difference isn't zero.
        return Boolean(
            downloadLimit !== null &&
                downloadLimitUsed !== null &&
                downloadLimit - downloadLimitUsed <= 0
        );
    };

    return (
        <RadioGroup defaultValue={String(value)}>
            <Stack>
                <React.Fragment>
                    {digitalRules.map((digitalRule, index) => {
                        const { name, id, resolution, videoResolution, slideshowResolution } =
                            digitalRule;
                        const downloadLimit = getDownloadLimit(digitalRule);
                        const downloadLimitUsed = getDownloadLimitUsed(digitalRule);
                        const disabled = isDisabled(downloadLimit, downloadLimitUsed);

                        const getPhotoSize = () => {
                            if (resolution === socialSize) {
                                return t('freeDigitalsSocialSize');
                            } else if (resolution === null) {
                                return t('freeDigitalsOriginalSize');
                            }

                            return t('error');
                        };

                        const getTipInfo = () => {
                            if (resolution === socialSize) {
                                return t('freeDigitalsSocialTipInfo');
                            } else if (resolution === null) {
                                return t('freeDigitalsOriginalTipInfo');
                            }

                            return t('error');
                        };

                        const showPhotoSize =
                            !isVideoDownload &&
                            !isSlideshowDownload &&
                            (resolution === socialSize || resolution === null);
                        const showVideoResolution = isVideoDownload && Boolean(videoResolution);
                        const showSlideshowResolution =
                            isSlideshowDownload && Boolean(slideshowResolution);

                        return (
                            <Box
                                key={id}
                                as="label"
                                htmlFor={`digital-rule-radio-${index + 1}`}
                                borderStyle="solid"
                                borderRadius="8px"
                                borderWidth="1px"
                                borderColor={value === String(index) ? 'brand.primary' : 'gray.200'}
                                cursor="pointer"
                                width="100%"
                                padding={4}
                                margin="0px"
                                opacity={disabled ? 0.5 : 1}
                                pointerEvents={disabled ? 'none' : 'auto'}
                            >
                                <Radio
                                    id={`digital-rule-radio-${index + 1}`}
                                    {...radioRegister}
                                    value={String(index)}
                                    colorScheme="brand.primary"
                                    isDisabled={disabled}
                                >
                                    <Heading as="h2" size="md" variant="primary">
                                        {name}
                                    </Heading>
                                </Radio>
                                {showPhotoSize && (
                                    <Flex alignItems="center" flexDirection="row">
                                        <Text
                                            marginLeft="20px"
                                            paddingRight="4px"
                                            fontWeight="normal"
                                        >
                                            {getPhotoSize()}
                                        </Text>
                                        <InfoTooltip
                                            infoIconLabel="Info"
                                            tooltipLabel={getTipInfo()}
                                        />
                                    </Flex>
                                )}
                                {showVideoResolution && (
                                    <Text marginLeft="20px" fontWeight="normal">
                                        {videoResolution}
                                    </Text>
                                )}
                                {showSlideshowResolution && (
                                    <Text marginLeft="20px" fontWeight="normal">
                                        {slideshowResolution}
                                    </Text>
                                )}
                                {downloadLimit !== null && downloadLimitUsed !== null && (
                                    <Text marginLeft="20px" fontWeight="normal">
                                        {/* Usage can exceed the limit (e.g. it was lowered
                                            after some downloads already happened) - floor at
                                            zero so this never renders a negative count. */}
                                        {Math.max(downloadLimit - downloadLimitUsed, 0)}{' '}
                                        {t('outOf')} {downloadLimit}{' '}
                                        {t('freeDigitalsRemainingDownload')}
                                    </Text>
                                )}
                            </Box>
                        );
                    })}
                </React.Fragment>
            </Stack>
        </RadioGroup>
    );
};

export default DigitalRules;
